import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../../../config/ApiConfig';
import Pagination10 from '../Pagination10';
import PageFooter from '../PageFooter';
import { formatString } from '../../utils/StringUtils';
import DefaultModal from '../modals/DefaultModal';
import useDefaultModal from '../hooks/useDefaultModal';
import ConfirmModal from '../modals/ConfirmModal';
import useConfirmModal from '../hooks/useConfirmModal';
import usePagination10 from '../hooks/usePagination10';
import LoadingModal from '../modals/LoadingModal';
import LoteModal from './LoteModal';
import TableContainer from '../TableContainer';
import TableHeader from '../TableHeader';
import useProgressModal from '../hooks/useProgressModal';
import ExcelUploadLoteModal from './ExcelUploadLoteModal';
import Toolbar from '../Toolbar';
import PageContent from '../PageContent';
import DfdLoteItensModal from './DfdLoteItensModal';

function DfdLotesModal({ isOpen, onClose, dfd }) {
  const [lotes, setLotes] = useState([]);
  const [isLoading, setLoading] = useState(false);
  const [shouldFetch, setShouldFetch] = useState(false);
  const [selectedLote, setSelectedLote] = useState(null);
  const [showLoteModal, setShowLoteModal] = useState(false);
  const [loteParaEditar, setLoteParaEditar] = useState(null);
  const [showExcelUploadModal, setShowExcelUploadModal] = useState(false);
  const [showLoteItensModal, setShowLoteItensModal] = useState(false);
  const [loteParaItens, setLoteParaItens] = useState(null);
  const token = localStorage.getItem('token');
  const cnpj = localStorage.getItem('cnpj');
  const { setDefaultModal, showDefaultModal } = useDefaultModal();
  const { setConfirmModal, showConfirmModal } = useConfirmModal();
  const { progressState } = useProgressModal();

  const totalItems = lotes.length;
  const { setPagination } = usePagination10(totalItems);

  const selectedLotes =
    lotes && setPagination
      ? lotes.slice(
          setPagination.startIndex,
          setPagination.startIndex + setPagination.rowsPerPage
        )
      : [];

  useEffect(() => {
    if (selectedLote && !selectedLotes.find(l => l.id === selectedLote.id)) {
      setSelectedLote(null);
    }
  }, [selectedLotes, selectedLote]);

  const handleRowClick = lote => {
    setSelectedLote(selectedLote && selectedLote.id === lote.id ? null : lote);
  };

  useEffect(() => {
    if (!isOpen) {
      setLotes([]);
      setSelectedLote(null);
      return;
    }
    if (!dfd?.cnpj) return;
    setLoading(true);
    axios
      .get(`${API_BASE_URL.DFD_LOTE}/all`, {
        params: { cnpj: cnpj, dfdNum: dfd.num },
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(result => {
        setLotes(result.data);
      })
      .catch(error => {
        console.error('Erro ao obter lotes:', error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [isOpen, dfd, cnpj, token, shouldFetch]);

  async function handleSaveLote(payload) {
    setLoading(true);
    try {
      const response = await axios.post(`${API_BASE_URL.DFD_LOTE}/save`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const loteData = response.data;
      if (payload.modo === 'editar' && payload.id) {
        setLotes(prev => prev.map(l => (l.id === loteData.id ? loteData : l)));
        showDefaultModal('success', 'Lote editado com sucesso!');
      } else {
        setLotes(prev => [...prev, loteData]);
        setSelectedLote(loteData);
        showDefaultModal('success', 'Lote cadastrado com sucesso!');
      }
    } catch (error) {
      showDefaultModal(
        'error',
        payload.modo === 'editar' ? 'Erro ao editar lote.' : 'Erro ao cadastrar lote.'
      );
    } finally {
      setLoading(false);
      setLoteParaEditar(null);
      setShowLoteModal(false);
    }
  }

  function handleInformation(lote) {
    axios
      .get(`${API_BASE_URL.TRAMITACAO}/justificativaDevolucao`, {
        params: { cnpjEntidade: lote.cnpj, dfdNum: lote.dfdNum },
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(result => {
        if (result.data) {
          showDefaultModal(
            'information',
            `**Justificativa da devolução:**\n ${result.data.justificativa}`
          );
        }
      })
      .catch(error => {
        console.error('Erro ao obter justificativa:', error);
        showDefaultModal('error', 'Erro ao obter justificativa!');
      });
  }

  function handleDelete(loteToDelete) {
    showConfirmModal('delete', () => handleDeleteConfirm(loteToDelete));
  }

  function handleDeleteConfirm(loteToDelete) {
    if (!loteToDelete) return;
    axios
      .get(`${API_BASE_URL.DFD_ITEM}/exists_lote`, {
        params: {
          cnpj: loteToDelete.cnpj,
          dfdNum: loteToDelete.dfdNum,
          loteNum: loteToDelete.id,
        },
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(result => {
        if (result.data.exists) {
          showDefaultModal(
            'error',
            'Este registro possui vínculos e não pode ser excluído. Exclua os vínculos e tente novamente.'
          );
        } else {
          axios
            .delete(`${API_BASE_URL.DFD_LOTE}/delete`, {
              params: {
                cnpj: loteToDelete.cnpj,
                dfdNum: loteToDelete.dfdNum,
                loteNum: loteToDelete.id,
              },
              headers: { Authorization: `Bearer ${token}` },
            })
            .then(() => {
              setLotes(prev => prev.filter(item => item.id !== loteToDelete.id));
              setSelectedLote(null);
              showDefaultModal('success', 'O registro foi excluído com sucesso');
            })
            .catch(error => {
              console.error('Erro ao excluir o item:', error);
              showDefaultModal('error', 'Erro ao excluir o registro!');
            });
        }
      });
  }

  async function handleExportToExcel() {
    try {
      const response = await axios.post(`${API_BASE_URL.DFD}/exportExcelLote`, dfd, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        responseType: 'blob',
      });
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = `DFD ${formatString(dfd?.num, 4)} LOTES.xlsx`;
      link.click();
    } catch (error) {
      console.error('Erro ao baixar o arquivo:', error);
    }
  }

  if (!isOpen) return null;

  return (
    <>
      <div
        className="modal fade show d-block"
        tabIndex="-1"
        style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1055 }}
      >
        <div className="modal-dialog modal-lg" style={{ maxWidth: '95vw', height: '90vh' }}>
          <div className="modal-content h-100">
            <div className="modal-header text-white py-1" style={{ background: 'linear-gradient(90deg, #0b3383, #0e48ad)' }}>
              <h6 className="modal-title m-0 small">
                <i className="bi bi-boxes me-2" />
                {`Lotes Cadastrados - DFD Nº ${dfd ? formatString(dfd.num, 4) : '--'}`}
              </h6>
              <button
                className="btn btn-link text-white p-0 mb-0"
                onClick={onClose}
                title="Fechar"
                style={{ fontSize: '0.9rem', textDecoration: 'none' }}
              >
                <i className="bi bi-x-lg"></i>
              </button>
            </div>
            <div
              className="modal-body p-0"
              style={{ overflow: 'hidden', display: 'flex', flexDirection: 'column', flex: 1 }}
            >
              <PageContent style={{ paddingBottom: 0 }}>
                <Toolbar
                  selectedId={selectedLote}
                  actions={[
                    { key: 'novo', label: 'Novo Lote', icon: 'bi-plus-circle', variant: 'success', onClick: () => { setLoteParaEditar(null); setShowLoteModal(true); } },
                    { key: 'editar', label: 'Editar', icon: 'bi-pencil-square', variant: 'primary', onClick: lote => { if (lote) { setLoteParaEditar(lote); setShowLoteModal(true); } }, requiresSelection: true },
                    { key: 'justificativa', label: 'Justificativa', icon: 'bi-info-circle', variant: 'warning', onClick: lote => lote && handleInformation(lote), show: selectedLote?.tramitacao === 'DEVOLVIDO PARA AJUSTES' },
                    { key: 'excluir', label: 'Excluir', icon: 'bi-trash3', variant: 'danger', onClick: lote => lote && handleDelete(lote), requiresSelection: true, disabled: !dfd || (dfd.tramitacao !== 'EM ELABORAÇÃO' && dfd.tramitacao !== 'DEVOLVIDO PARA AJUSTES') },
                    { key: 'export', label: 'Exportar Excel', icon: 'bi-file-earmark-excel', variant: 'info', onClick: handleExportToExcel, disabled: progressState.show },
                    { key: 'import', label: 'Importar Excel', icon: 'bi-file-earmark-arrow-down', variant: 'warning', onClick: () => setShowExcelUploadModal(true), show: dfd?.tramitacao === 'EM ELABORAÇÃO' || dfd?.tramitacao === 'DEVOLVIDO PARA AJUSTES', disabled: progressState.show },
                    { key: 'itens', label: 'Itens', icon: 'bi-list-ul', variant: 'primary', onClick: lote => { if (lote) { setLoteParaItens(lote); setShowLoteItensModal(true); } }, requiresSelection: true },
                  ]}
                />

                <LoadingModal loading={isLoading} />

                <TableContainer tableClass="table-custom-radius">
                  <TableHeader columns={[
                    { label: 'Nº', width: '5%' },
                    { label: 'Descrição' },
                    { label: 'Qtd. Itens', width: '10%', align: 'center' },
                  ]} />
                  <tbody>
                    {!isLoading && selectedLotes.length > 0 ? (
                      selectedLotes.map(c => {
                        const isActiveRow = selectedLote && selectedLote.id === c.id;
                        return (
                          <tr
                            key={`${c.cnpj}-${c.dfdNum}-${c.id}`}
                            className={isActiveRow ? 'table-active' : ''}
                            style={{ cursor: 'pointer', transition: 'background-color 0.2s ease' }}
                            onClick={() => handleRowClick(c)}
                            title={isActiveRow ? 'Clique novamente para desmarcar' : 'Clique para selecionar'}
                          >
                            <td style={{ width: '5%' }}>
                              <span className={isActiveRow ? 'fw-bold text-primary' : ''}>{c.id}</span>
                            </td>
                            <td>
                              <span className={isActiveRow ? 'fw-bold text-primary' : ''}>{c.descricao}</span>
                            </td>
                            <td className="text-center" style={{ width: '10%' }}>
                              <span className={isActiveRow ? 'fw-bold text-primary' : ''}>{c.dfdItemSet?.length || 0}</span>
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan="3" className="text-center">
                          Nenhum registro encontrado.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </TableContainer>
                <LoteModal
                  isOpen={showLoteModal}
                  dfd={dfd}
                  lote={loteParaEditar}
                  onClose={() => { setShowLoteModal(false); setLoteParaEditar(null); }}
                  onSuccess={handleSaveLote}
                />
                <ExcelUploadLoteModal
                  show={showExcelUploadModal}
                  onClose={() => setShowExcelUploadModal(false)}
                  onSuccess={() => setShouldFetch(prev => !prev)}
                  dfd={dfd}
                />

                <DefaultModal {...setDefaultModal} />
                <ConfirmModal {...setConfirmModal} />
              </PageContent>
              <PageFooter>
                <Pagination10 {...setPagination}/>
              </PageFooter>
            </div>
          </div>
        </div>
      </div>

      <DfdLoteItensModal
        isOpen={showLoteItensModal}
        onClose={() => { setShowLoteItensModal(false); setLoteParaItens(null); }}
        dfd={dfd}
        dfdLote={loteParaItens}
      />
    </>
  );
}

export default DfdLotesModal;
